import React, { useMemo, useState } from 'react';
import { CalendarInputCommonProps } from './types';
import { DateInput } from './DateInput';
import { useFocusRing } from '@react-aria/focus';
import {
  input_default_style,
  input_focus_style,
  input_disabled_style,
  input_error_style,
  getInputSizeStyle,
  input_wrapper_base_css,
} from '../../input/styles';
import classNames from 'classnames';
import {
  range_picker_focus_style,
  getRangeFocusInputStyle,
  range_input_default_style,
  range_input_disabled_style,
  date_picker_input_entered_disabled_style,
  getDatePickerInputWrapperStyleBySize,
} from '../styles';
import { IconArrowDirectionRight } from '../../icons/generated';
import { semantic_colors } from '../../../foundation/semantic_colors';
import { DatePickerIcon } from '../DatePickerIcon';
import { useProviderConfig } from '../../provider';
import { isCursorItemComplete, isCursorPartiallyValid } from './utils';
import { InputProps } from '../../input';
import { Stack } from '../../stack';

export interface DateRangeInputProps extends CalendarInputCommonProps, Pick<InputProps, 'width' | 'size' | 'flex'> {
  className?: string;
  format?: string;
  disabled?: boolean;
  error?: boolean;
  readOnly?: boolean;
  /**
   * The target properties to write when the user manually types the date.
   */
  writeTarget?: 'from' | 'to' | 'all';
  onFocus: (target: 'from' | 'to') => void;
  onClick: (target: 'from' | 'to') => void;
  showRemoveButton?: boolean;
  onRemove?: () => void;
}

export const DateRangeInput: React.FC<DateRangeInputProps> = React.forwardRef(
  (
    {
      onFocus,
      onClick,
      onRemove,
      className,
      disabled,
      error: propsError,
      readOnly,
      showRemoveButton,
      width,
      flex,
      ...props
    }: DateRangeInputProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [isHovering, setIsHovering] = useState(false);
    const { locale } = useProviderConfig();
    const { cursor, target, size = 'medium', constraint, items, tolerateInvalidValues } = props;
    const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
    const isError = useMemo(
      () =>
        cursor?.typingInvalid ||
        propsError ||
        (!tolerateInvalidValues && !isCursorPartiallyValid(constraint, { items }, cursor)),
      [constraint, items, cursor, propsError, tolerateInvalidValues],
    );
    const status_style = {
      ...input_default_style,
      ...(isFocusVisible && input_focus_style),
      ...(disabled && input_disabled_style),
      ...(isError && input_error_style),
    };
    const isFromDisabled = target === 'to' || disabled;
    const isToDisabled = target === 'from' || disabled;
    return (
      <Stack width={width} flex={flex} ref={ref}>
        <div
          className={classNames('pds-date-range-input', className)}
          css={[
            input_wrapper_base_css,
            getInputSizeStyle(size),
            status_style,
            range_picker_focus_style,
            getRangeFocusInputStyle(size),
            getDatePickerInputWrapperStyleBySize(size),
          ]}
          {...(props.cursor?.focus != null && focusProps)}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <DateInput
            {...props}
            target='from'
            placeholder={locale?.DatePickerRange.placeholder[0]}
            onFocus={() => onFocus('from')}
            onClick={() => onClick('from')}
            className={classNames('start', { focused: target !== 'all' && cursor?.focus === 'from', error: isError })}
            style={range_input_default_style}
            css={[
              {
                ...(isFromDisabled && range_input_disabled_style),
                ...(isFromDisabled && cursor?.item?.from && date_picker_input_entered_disabled_style),
              },
            ]}
            disabled={isFromDisabled}
            readOnly={readOnly}
            rightIcon={
              <IconArrowDirectionRight
                size={12}
                color={disabled || target != null ? semantic_colors.content.disabled : semantic_colors.content.tertiary}
              />
            }
          />
          <DateInput
            {...props}
            target='to'
            placeholder={locale?.DatePickerRange.placeholder[1]}
            onFocus={() => onFocus('to')}
            onClick={() => onClick('to')}
            className={classNames('end', { focused: target !== 'all' && cursor?.focus === 'to', error: isError })}
            error={propsError}
            style={range_input_default_style}
            css={[
              {
                ...(isToDisabled && range_input_disabled_style),
                ...(isToDisabled && cursor?.item?.to && date_picker_input_entered_disabled_style),
              },
            ]}
            disabled={isToDisabled}
            readOnly={readOnly}
            rightIcon={
              <DatePickerIcon
                showRemoveIcon={showRemoveButton && isHovering && isCursorItemComplete(cursor?.item)}
                onClick={onRemove}
              />
            }
          />
        </div>
      </Stack>
    );
  },
);
